import { Breadcrumbs, Button, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Typography } from '@material-ui/core';
import { BugReport as BugReportIcon, MoreVert as MoreVertIcon, NavigateNext as NavigateNextIcon } from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/styles';
import React, { CSSProperties, useContext, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import LocalizationContext from '../../contexts/LocalizationContext';
import { PageType } from '../../interfaces/IAppDefinition';
import HelperMenu from './HelperMenu';
import { TeamContext } from './../apps/edp/team/contexts/TeamProvider';
import { IApps, ISubAppLayouts } from '../apps/account/interfaces/IAppLayouts';

export interface BreadcrumbPath {
    textFr: string;
    textEn: string;
    url: string;
    app?: IApps | ISubAppLayouts;
    disabled: boolean;
    type: PageType;
    siblings?: ISubAppLayouts[];
}

export interface OptionMenuItem {
    text: string;
    icon: JSX.Element;
    onClick: () => void;
}

interface AppHeaderProps {
    name: string;
    style?: CSSProperties;
    breadcrumbs?: BreadcrumbPath[];
    optionMenuItems?: OptionMenuItem[];
    appName?: string;
    subappName?: string;
    appId?: number;
    subAppId?: number | null;
    [x: string]: any;
}

const useStyles = makeStyles({
    breadcrumbs: {
        marginBottom: '6px',
    },
    appTitle: {
        fontFamily: 'Gellix',
        fontWeight: 500,
        fontSize: '48px',
        paddingBottom: '9px',
    },
    moreOptions: {
        padding: 0,
        width: '48px',
        height: '48px',
        minWidth: 'unset',
        margin: 'auto 0',
        marginLeft: '8px',
    },
    optionsMenu: {},
    siblingText: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: '1.5',
        marginRight: '6px',
    },
});

const AppHeader = ({ appName, subappName, name, style, breadcrumbs, optionMenuItems, appId, subAppId, ...otherProps }: AppHeaderProps) => {
    const classes = useStyles();
    const localization = useContext(LocalizationContext);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const history = useHistory();
    const theme = useTheme<any>();
    const team = useContext(TeamContext);
    const menuItems: OptionMenuItem[] = [
        ...(optionMenuItems || []),
        {
            text: localization.strings.reportAnIssue,
            icon: <BugReportIcon />,
            onClick: () => {
                window.location.href = `mailto:support@bba.ca?Subject=${localization.strings.appHeader.subject}&body=${localization.strings.appHeader.bodyEmail}`;
            },
        },
    ];

    return (
        <>
            {breadcrumbs && (
                <Breadcrumbs separator={<NavigateNextIcon fontSize='small' />} className={classes.breadcrumbs}>
                    <Link to='/' className='no-link no-link-opacity'>
                        Apps
                    </Link>
                    {breadcrumbs.map((path: BreadcrumbPath) =>
                        path.url ? (
                            path.siblings && path.siblings.length > 0 ? (
                                <TextField
                                    key={path.textEn}
                                    select
                                    variant='outlined'
                                    size='small'
                                    style={{ marginTop: '4px', display: 'flex', paddingTop: 0, paddingBottom: 0 }}
                                    defaultValue={path.app?.name}
                                    className='textbox-no-underline'
                                    onChange={(e) => {
                                        if (!path) return;
                                        const sibling: ISubAppLayouts | undefined = path?.siblings?.find((s) => s?.name === e.target.value);
                                        const url: string | undefined = sibling?.url;
                                        if (url !== undefined) history.push(`/${url}`);
                                    }}>
                                    {path.siblings.map((sibling: ISubAppLayouts) => (
                                        <MenuItem key={sibling.url} value={sibling.name} disabled={!sibling.enabled}>
                                            <div style={{ display: 'flex' }}>
                                                <div className={classes.siblingText}>
                                                    {localization.lang === 'fr' ? sibling.nameFr : sibling.nameEn}
                                                </div>
                                            </div>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : (
                                <Link key={path.url} to={path.url} className='no-link no-link-opacity'>
                                    {localization.lang === 'fr' ? path.textFr : path.textEn}
                                </Link>
                            )
                        ) : (
                            <Typography key={path.url}>{localization.lang === 'fr' ? path.textFr : path.textEn}</Typography>
                        )
                    )}
                </Breadcrumbs>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <Typography
                    {...otherProps}
                    variant='h4'
                    className={classes.appTitle}
                    style={{ display: 'inline-block', color: theme.palette.primary.main, ...style }}>
                    {name}
                </Typography>
                <div style={{ display: 'flex' }}>
                    <HelperMenu
                        appName={appName}
                        subAppName={subappName}
                        isManager={team?.isManager ?? false}
                        appId={appId}
                        subAppId={subAppId}
                    />
                    <Button onClick={(e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget)} className={classes.moreOptions}>
                        <MoreVertIcon />
                    </Button>
                </div>
                <Menu open={Boolean(menuAnchor)} anchorEl={menuAnchor} keepMounted onClose={() => setMenuAnchor(null)}>
                    {menuItems &&
                        menuItems.map((menuItem) => (
                            <MenuItem
                                key={menuItem.text}
                                className={classes.optionsMenu}
                                onClick={() => {
                                    setMenuAnchor(null);
                                    menuItem.onClick();
                                }}>
                                <ListItemIcon>{menuItem.icon}</ListItemIcon>
                                <ListItemText primary={menuItem.text} />
                            </MenuItem>
                        ))}
                </Menu>
            </div>
        </>
    );
};

export default AppHeader;
